import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CommandItem } from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { OptionList, type OptionListOption } from '@/components/ui/option-list'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { advanceFromKbdField, kbdFieldProps } from '@/hooks/formKeyboardNav'
import { cn } from '@/lib/utils'
import { ChevronDown, Plus, X } from 'lucide-react'
import { type ReactNode, useMemo, useRef, useState } from 'react'

const DEFAULT_MAX_DISPLAY_ITEMS = 3
const ALL_VALUE = '__all__'

export interface PickerBaseProps {
	options: OptionListOption[]
	/**
	 * How a multi select trigger shows what is picked. Ignored in single mode, which shows the label.
	 * - `compact`: the first `maxDisplayItems` badges, then a remainder count
	 * - `chips`: every badge, each removable
	 */
	variant?: 'compact' | 'chips'
	maxDisplayItems?: number
	placeholder?: string
	label?: string
	labelClassName?: string
	/** A string shows the red border and the message below it; `true` shows only the border, for a caller that renders its own message. */
	error?: string | boolean
	disabled?: boolean
	loading?: boolean
	clearable?: boolean
	searchPlaceholder?: string
	emptyMessage?: ReactNode
	/** Notified as the search changes. The list still filters on its own unless `remoteSearch` is set. */
	onSearchChange?: (search: string) => void
	/** The caller owns matching: `options` is the result window and is never filtered again here. */
	remoteSearch?: boolean
	/** Ask for more as the list nears its end, for a caller that loads a page at a time. */
	paging?: { onReachEnd: () => void; loadingMore?: boolean }
	/** Total matches upstream, so a partly loaded list does not announce itself as complete. */
	totalCount?: number
	/** Keeps the list inside an enclosing dialog rather than the viewport. */
	constrainToDialog?: boolean
	/** Tags the trigger for dialog keyboard navigation, so a pick moves focus to the next field. */
	enableKeyboardNav?: boolean
	/** Values that cannot be removed: no remove control, and clearing keeps them. */
	lockedValues?: string[]
	/** Hover copy on a locked badge. */
	lockedTooltip?: ReactNode
	/** Adds a row that selects every option, and clears them when picked again. */
	allOption?: { label?: string }
	/** Most values that may be picked. A pick beyond it is refused. */
	maxItems?: number
	onMaxLimitReached?: () => void
	/** Offers whatever was typed as a value of its own, for a list that cannot enumerate everything. */
	create?: {
		label?: (query: string) => string
		/** Returning a reason hides the create row and shows the reason in its place. */
		blockedMessage?: (query: string) => string | undefined
	}
	/** A button below an empty list, for making the missing option elsewhere. Picking it closes the list. */
	emptyAction?: { label: string; onSelect: () => void; 'data-testid'?: string }
	/** Values that leave the list open when picked, rather than closing it. */
	keepOpenValues?: string[]
	/** Hover copy on a disabled row that carries no reason of its own. */
	disabledOptionTooltip?: ReactNode
	/** Whether the search also matches option values. Off where a value is an id the viewer never sees. */
	searchByValue?: boolean
	/** Hover and focus copy on the trigger. Held back while the list is open, and after a pick with the pointer. */
	triggerTooltip?: ReactNode
	popoverClassName?: string
	className?: string
	/** Trigger id, for a label to point at. */
	id?: string
	'aria-label'?: string
	'aria-invalid'?: boolean | 'true' | 'false'
	/** Explicit keyboard field tag, for a caller that names its own field order. */
	'data-kbd-field'?: string
	'data-testid'?: string
}

interface SinglePickerProps extends PickerBaseProps {
	mode: 'single'
	value: string
	onValueChange: (value: string) => void
}

interface MultiPickerProps extends PickerBaseProps {
	mode: 'multi'
	values: string[]
	onChange: (values: string[]) => void
}

export type PickerProps = SinglePickerProps | MultiPickerProps

/**
 * An action nested inside the trigger button, where a real <button> would be invalid HTML. Pointer
 * down is swallowed so the popover does not toggle, and Enter and Space stand in for the button
 * semantics the element cannot have.
 */
function TriggerAction({
	label,
	className,
	testId,
	onAct,
	children,
}: {
	label: string
	className: string
	testId?: string
	onAct: () => void
	children: ReactNode
}) {
	return (
		<span
			// biome-ignore lint/a11y/useSemanticElements: nested <button> is invalid inside the trigger button
			role='button'
			tabIndex={0}
			aria-label={label}
			data-testid={testId}
			className={className}
			onPointerDown={event => {
				event.preventDefault()
				event.stopPropagation()
			}}
			onClick={event => {
				event.preventDefault()
				event.stopPropagation()
				onAct()
			}}
			onKeyDown={event => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault()
					onAct()
				}
			}}>
			{children}
		</span>
	)
}

const matches = (option: OptionListOption, search: string, byValue: boolean) =>
	option.label.toLowerCase().includes(search) || (byValue && option.value.toLowerCase().includes(search))

function PickerTrigger({
	tooltip,
	listOpen,
	disabled,
	children,
}: {
	tooltip?: ReactNode
	listOpen: boolean
	disabled: boolean
	children: ReactNode
}) {
	const [open, setOpen] = useState(false)
	if (!tooltip) {
		return <PopoverTrigger asChild>{children}</PopoverTrigger>
	}
	return (
		<Tooltip open={open && !listOpen} onOpenChange={setOpen}>
			{/* `disabled` here lets the tooltip wrap a disabled trigger, which takes no pointer events. */}
			<TooltipTrigger asChild className='w-full'>
				<PopoverTrigger asChild disabled={disabled}>
					{children}
				</PopoverTrigger>
			</TooltipTrigger>
			<TooltipContent side='bottom' align='start' className='max-w-sm text-pretty'>
				{tooltip}
			</TooltipContent>
		</Tooltip>
	)
}

/**
 * A form control over `OptionList`: a trigger showing what is picked, and the list in a popover.
 * Single mode replaces the value and closes; multi mode accumulates and stays open.
 */
export function Picker(props: PickerProps) {
	const { options, mode } = props
	const values = props.mode === 'single' ? (props.value === '' ? [] : [props.value]) : props.values
	const onChange = (next: string[]) => {
		if (props.mode === 'single') {
			props.onValueChange(next[0] ?? '')
			return
		}
		props.onChange(next)
	}
	const {
		variant = 'compact',
		maxDisplayItems = DEFAULT_MAX_DISPLAY_ITEMS,
		placeholder = 'Select',
		label,
		labelClassName,
		error,
		disabled,
		loading,
		clearable,
		searchPlaceholder = 'Search...',
		emptyMessage = 'No results found.',
		onSearchChange,
		remoteSearch,
		paging,
		totalCount,
		constrainToDialog = true,
		enableKeyboardNav,
		lockedValues,
		lockedTooltip,
		allOption,
		maxItems,
		onMaxLimitReached,
		create,
		emptyAction,
		keepOpenValues,
		disabledOptionTooltip,
		searchByValue = true,
		triggerTooltip,
		popoverClassName,
		className,
		id,
		'aria-label': ariaLabel,
		'aria-invalid': ariaInvalid,
		'data-kbd-field': dataKbdField,
		'data-testid': testId,
	} = props
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [collisionBoundary, setCollisionBoundary] = useState<HTMLElement | null>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const advanceOnCloseRef = useRef(false)

	const locked = useMemo(() => new Set(lockedValues ?? []), [lockedValues])
	const optionLabels = useMemo(() => new Map(options.map(option => [option.value, option.label])), [options])
	const labelFor = (value: string) => optionLabels.get(value) ?? value

	// A disabled option is one the caller refuses row by row, commonly for access. Select all must not
	// grant what an individual pick would not.
	const selectableValues = useMemo(
		() => options.filter(option => !option.disabled).map(option => option.value),
		[options],
	)
	const allSelected = selectableValues.length > 0 && selectableValues.every(value => values.includes(value))

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase()
		const shown =
			remoteSearch || query === '' ? options : options.filter(option => matches(option, query, searchByValue))
		const withTooltip = disabledOptionTooltip
			? shown.map(option =>
					option.disabled && option.disabledReason === undefined
						? { ...option, disabledReason: disabledOptionTooltip }
						: option,
				)
			: shown
		// The all row is dropped with nothing to select, so the list falls through to the empty message.
		return allOption && shown.length > 0
			? [{ value: ALL_VALUE, label: allOption.label ?? 'All' }, ...withTooltip]
			: withTooltip
	}, [options, search, allOption, disabledOptionTooltip, remoteSearch, searchByValue])

	const updateSearch = (next: string) => {
		setSearch(next)
		onSearchChange?.(next)
	}

	const query = search.trim()
	const createBlocked = create && query !== '' ? create.blockedMessage?.(query) : undefined
	const showCreate =
		create !== undefined &&
		query !== '' &&
		createBlocked === undefined &&
		!options.some(option => option.value.toLowerCase() === query.toLowerCase())

	const commit = (value: string) => {
		if (mode === 'single') {
			onChange([value])
			if (!keepOpenValues?.includes(value)) {
				setOpen(false)
			}
			return
		}
		onChange(values.includes(value) ? values : [...values, value])
	}

	const select = (value: string) => {
		if (enableKeyboardNav) {
			advanceOnCloseRef.current = true
		}
		if (value === ALL_VALUE) {
			onChange(allSelected ? clearedValues() : [...new Set([...values, ...selectableValues])])
			return
		}
		if (mode === 'single') {
			commit(value)
			return
		}
		if (values.includes(value)) {
			if (locked.has(value)) {
				return
			}
			onChange(values.filter(v => v !== value))
			return
		}
		if (maxItems !== undefined && values.length >= maxItems) {
			onMaxLimitReached?.()
			// Closing is the signal that the pick was refused; the list would otherwise look inert.
			setOpen(false)
			return
		}
		onChange([...values, value])
	}

	const remove = (value: string) => onChange(values.filter(v => v !== value))

	const clearedValues = () => values.filter(value => locked.has(value))

	const clearAll = () => onChange(clearedValues())

	const badge = (value: string) => {
		const isLocked = locked.has(value)
		const rendered = (
			<Badge
				key={value}
				variant='secondary'
				className='pointer-events-auto flex h-5 items-center gap-1 rounded-md border-transparent bg-accent px-2.5 py-0 text-accent-foreground text-body font-medium'
				data-testid={testId ? `${testId}-badge-${value}` : undefined}>
				<span className='truncate'>{labelFor(value)}</span>
				{!isLocked && (
					<TriggerAction
						label={`Remove ${labelFor(value)}`}
						testId={testId ? `${testId}-badge-${value}-remove` : undefined}
						className='-mr-1 inline-flex size-(--status-icon-size-sm) shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-accent-foreground/15'
						onAct={() => remove(value)}>
						<X className='size-(--icon-badge-remove)' />
					</TriggerAction>
				)}
			</Badge>
		)
		return isLocked && lockedTooltip ? (
			<Tooltip key={value}>
				<TooltipTrigger asChild>{rendered}</TooltipTrigger>
				<TooltipContent>{lockedTooltip}</TooltipContent>
			</Tooltip>
		) : (
			rendered
		)
	}

	const triggerContent = () => {
		if (values.length === 0) {
			return <span className='truncate text-muted-foreground'>{placeholder}</span>
		}
		if (mode === 'single') {
			return <span className='truncate'>{labelFor(values[0])}</span>
		}
		if (variant === 'chips') {
			return <div className='pointer-events-none flex max-w-full flex-wrap gap-1'>{values.map(badge)}</div>
		}
		const shown = values.slice(0, maxDisplayItems)
		const remaining = values.length - shown.length
		return (
			<div className='pointer-events-none flex max-w-full items-center gap-1 overflow-hidden'>
				{shown.map(badge)}
				{remaining > 0 && <span className='shrink-0 text-body text-muted-foreground'>+{remaining}</span>}
			</div>
		)
	}

	return (
		<div className={cn('w-full space-y-2', className)}>
			{label && <Label className={cn('dashboard-label font-medium', labelClassName)}>{label}</Label>}
			<Popover
				open={open}
				onOpenChange={next => {
					if (next) {
						advanceOnCloseRef.current = false
						setCollisionBoundary(
							constrainToDialog ? (triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null) : null,
						)
					} else {
						updateSearch('')
						if (enableKeyboardNav && advanceOnCloseRef.current && triggerRef.current) {
							const trigger = triggerRef.current
							requestAnimationFrame(() => advanceFromKbdField(trigger))
						}
					}
					setOpen(next)
				}}>
				<PickerTrigger tooltip={triggerTooltip} listOpen={open} disabled={Boolean(disabled || loading)}>
					<Button
						ref={triggerRef}
						variant='outline'
						// biome-ignore lint/a11y/useSemanticElements: ARIA combobox role required for an accessible dropdown
						role='combobox'
						aria-expanded={open}
						aria-haspopup='listbox'
						id={id}
						aria-label={ariaLabel}
						aria-invalid={ariaInvalid ?? (error ? true : undefined)}
						data-kbd-field={dataKbdField}
						disabled={disabled || loading}
						data-testid={testId}
						{...(enableKeyboardNav ? kbdFieldProps('select') : {})}
						className={cn(
							'h-auto min-h-(--height-control) w-full justify-between gap-2 bg-card px-3 py-0.5 text-body hover:bg-transparent',
							error && 'ring-1 ring-destructive',
						)}>
						<div className='min-w-0 flex-1 overflow-hidden text-left'>{triggerContent()}</div>
						<div className='ml-2 flex shrink-0 items-center gap-1'>
							{clearable && values.length > 0 && !disabled && (
								<TriggerAction
									label='Clear'
									testId={testId ? `${testId}-clear` : undefined}
									className='pointer-events-auto inline-flex cursor-pointer items-center justify-center rounded-sm opacity-50 hover:opacity-100'
									onAct={clearAll}>
									<X className='size-(--icon-standalone) text-muted-foreground' />
								</TriggerAction>
							)}
							<ChevronDown className='size-(--icon-standalone) shrink-0 text-muted-foreground' />
						</div>
					</Button>
				</PickerTrigger>
				<PopoverContent
					align='start'
					collisionBoundary={collisionBoundary ?? undefined}
					collisionPadding={8}
					className={cn('z-(--z-popover) w-(--radix-popover-trigger-width) p-0', popoverClassName)}
					data-testid={testId ? `${testId}-popover` : undefined}
					// The list portals outside any open dialog, whose scroll lock would otherwise swallow the wheel.
					onWheel={event => event.stopPropagation()}
					onOpenAutoFocus={event => {
						event.preventDefault()
						searchInputRef.current?.focus()
					}}>
					<OptionList
						leadingRow={
							showCreate ? (
								<CommandItem
									value={`create:${query}`}
									onSelect={() => {
										commit(query)
										updateSearch('')
									}}
									data-testid={testId ? `${testId}-create` : undefined}
									className='flex cursor-pointer items-center gap-2'>
									<Plus className='size-(--icon-standalone)' />
									<span className='truncate text-body'>{create?.label?.(query) ?? `Create "${query}"`}</span>
								</CommandItem>
							) : undefined
						}
						emptyMessage={
							createBlocked !== undefined ? (
								<output
									data-testid={testId ? `${testId}-create-blocked` : undefined}
									className='block px-2 py-4 text-center text-body text-destructive'>
									{createBlocked}
								</output>
							) : (
								emptyMessage
							)
						}
						label={label ?? placeholder}
						options={filtered}
						values={allOption && allSelected ? [ALL_VALUE, ...values] : values}
						mode={mode}
						onSelect={select}
						loading={loading}
						listClassName='max-h-60'
						totalCount={totalCount}
						onScroll={
							paging
								? event => {
										const el = event.currentTarget
										if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
											paging.onReachEnd()
										}
									}
								: undefined
						}
						footer={
							paging?.loadingMore ? (
								<div className='flex justify-center py-2'>
									<Spinner className='size-(--icon-standalone)' />
								</div>
							) : emptyAction && filtered.length === 0 ? (
								<div className='border-t border-border p-1'>
									<Button
										variant='ghost'
										size='sm'
										className='w-full justify-start gap-2 text-body'
										data-testid={emptyAction['data-testid']}
										onClick={() => {
											setOpen(false)
											emptyAction.onSelect()
										}}>
										<Plus className='size-(--icon-standalone)' />
										{emptyAction.label}
									</Button>
								</div>
							) : undefined
						}
						optionTestId={value => (testId ? `${testId}-option-${value}` : '')}
						search={{
							value: search,
							onChange: updateSearch,
							placeholder: searchPlaceholder,
							inputRef: searchInputRef,
							testId: testId ? `${testId}-search` : undefined,
							clearTestId: testId ? `${testId}-search-clear` : undefined,
							clearLabel: 'Clear search',
						}}
					/>
				</PopoverContent>
			</Popover>
			{typeof error === 'string' && error && <p className='text-body text-destructive'>{error}</p>}
		</div>
	)
}
